import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, CircularProgress, Alert, Chip, Tooltip, IconButton, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import EditableTable from '../components/common/EditableTable';
import RequestQuotesDialog from '../components/common/RequestQuotesDialog';
import { quoteColumn } from '../components/common/equipmentQuoteCells';
import { equipmentsAPI, engineeringAPI, configAPI, costEntriesAPI, offersAPI, suppliersAPI } from '../services/api';

const equipmentColumns = [
  { field: 'offer_label', header: 'Oferta', width: '7%' },
  { field: 'tag', header: 'Tag', width: '8%' },
  { field: 'name', header: 'Equipo', width: '20%' },
  { field: 'capacity', header: 'Capacidad' },
  { field: 'material', header: 'Material' },
  { field: 'power', header: 'Potencia' },
  { field: 'supplier', header: 'Proveedor' },
  quoteColumn,
  { field: 'estimated_cost', header: 'Coste Real (EUR)', type: 'number' },
  { field: 'offer_cost', header: 'Coste Oferta (EUR)', type: 'number' },
  { field: 'delivery_weeks', header: 'Plazo (sem)', type: 'number' },
];

const equipmentFields = [
  { field: 'tag', label: 'Tag (ej: TK-001)' },
  { field: 'name', label: 'Nombre del equipo', required: true },
  { field: 'description', label: 'Descripcion', fullWidth: true },
  { field: 'capacity', label: 'Capacidad / Tamano' },
  { field: 'material', label: 'Material constructivo' },
  { field: 'power', label: 'Potencia instalada' },
  { field: 'dimensions', label: 'Dimensiones (LxWxH)' },
  { field: 'supplier', label: 'Proveedor' },
  { field: 'supplier_reference', label: 'Referencia proveedor' },
  { field: 'estimated_cost', label: 'Coste Real (EUR)', type: 'number' },
  { field: 'offer_cost', label: 'Coste Oferta (EUR)', type: 'number' },
  { field: 'delivery_weeks', label: 'Plazo entrega (semanas)', type: 'number' },
  { field: 'notes', label: 'Notas / especificaciones', multiline: true, rows: 3, fullWidth: true },
];

const COST_STATUS_OPTIONS = [
  { value: 'estimado',    label: 'Estimado' },
  { value: 'comprometido', label: 'Comprometido' },
  { value: 'facturado',   label: 'Facturado' },
  { value: 'cerrado',     label: 'Cerrado' },
];
const COST_STATUS_COLOR_MAP = {
  estimado:    'default',
  comprometido: 'warning',
  facturado:   'primary',
  cerrado:     'success',
};
const COST_STATUS_LABEL_MAP = {
  estimado:    'Estimado',
  comprometido: 'Comprometido',
  facturado:   'Facturado',
  cerrado:     'Cerrado',
};

const costColumns = [
  { field: 'offer_label', header: 'Oferta', width: '8%' },
  { field: 'concept', header: 'Concepto / Partida', width: '35%' },
  { field: 'real_cost_status', header: 'Estado', type: 'chip', width: '12%', colorMap: COST_STATUS_COLOR_MAP, labelMap: COST_STATUS_LABEL_MAP },
  { field: 'real_cost', header: 'Coste Real (EUR)', type: 'number' },
  { field: 'offer_cost', header: 'Coste Oferta (EUR)', type: 'number' },
];

const EMPTY_FORM = {
  process_flow_description: '', pid_description: '', technical_specs: '',
  estimated_duration_weeks: '', notes: '', real_cost: '', offer_cost: '',
};

export default function BasicEngineering({ projectId, projectType, approvedAmount = 0 }) {
  const [equipments, setEquipments] = useState([]);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState([]);
  const [rfqOpen, setRfqOpen] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [costs, setCosts] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [offers, setOffers] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (projectId) loadData();
  }, [projectId]);

  useEffect(() => {
    if (projectType) loadPartidas();
  }, [projectType]);

  const loadPartidas = async () => {
    try {
      const resp = await configAPI.listCostPartidas(projectType);
      setPartidas(resp.data);
    } catch (err) {
      console.error('Error loading partidas:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const equipmentsResp = await equipmentsAPI.list(projectId);
      // Load offers to resolve offer labels
      let offersData = [];
      try { const oResp = await offersAPI.list(projectId); offersData = oResp.data; } catch (e) { offersData = []; }
      setOffers(offersData);
      // Annotate equipments with offer_label
      const annotateOffer = (item) => {
        if (!item.offer_id) return { ...item, offer_label: 'Básica' };
        const off = offersData.find((o) => o.id === item.offer_id);
        return { ...item, offer_label: off ? `Oferta ${off.offer_number}` : `#${item.offer_id}` };
      };
      setEquipments(equipmentsResp.data.map(annotateOffer));

      // Load cost entries (partidas)
      try {
        const ceResp = await costEntriesAPI.list(projectId);
        // Annotate cost entries with offer_label
        setCosts(ceResp.data.map((item) => {
          if (!item.offer_id) return { ...item, offer_label: 'Básica' };
          const off = offers.find((o) => o.id === item.offer_id) ||
                      offersData.find((o) => o.id === item.offer_id);
          return { ...item, offer_label: off ? `Oferta ${off.offer_number}` : `#${item.offer_id}` };
        }));
      } catch (e) {
        setCosts([]);
      }

      // Load or create basic engineering record
      try {
        const bResp = await engineeringAPI.getBasic(projectId);
        setFormData({
          process_flow_description: bResp.data.process_flow_description || '',
          pid_description: bResp.data.pid_description || '',
          technical_specs: bResp.data.technical_specs || '',
          estimated_duration_weeks: bResp.data.estimated_duration_weeks || '',
          notes: bResp.data.notes || '',
          real_cost: bResp.data.real_cost ?? '',
          offer_cost: bResp.data.offer_cost ?? '',
        });
      } catch (e) {
        // 404 = not yet created, create it
        await engineeringAPI.createBasic({ project_id: projectId });
      }

      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar datos.');
    } finally {
      setLoading(false);
    }
  };

  const formRef = React.useRef(formData);
  React.useEffect(() => { formRef.current = formData; }, [formData]);

  const handleSave = async () => {
    const data = formRef.current;
    try {
      setSaving(true);
      await engineeringAPI.updateBasic(projectId, {
        project_id: projectId,
        process_flow_description: data.process_flow_description || null,
        pid_description: data.pid_description || null,
        technical_specs: data.technical_specs || null,
        estimated_duration_weeks: data.estimated_duration_weeks
          ? parseInt(data.estimated_duration_weeks) : null,
        notes: data.notes || null,
        real_cost: data.real_cost !== '' ? parseFloat(data.real_cost) : null,
        offer_cost: data.offer_cost !== '' ? parseFloat(data.offer_cost) : null,
      });
    } catch (err) {
      console.error('Error saving:', err);
      setError('Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  useEffect(() => {
    suppliersAPI.list()
      .then((resp) => setSupplierOptions(resp.data.map((sup) => ({ id: sup.id, value: sup.name, label: sup.name }))))
      .catch(() => {});
  }, []);

  const loadEquipments = async () => {
    const resp = await equipmentsAPI.list(projectId);
    setEquipments(resp.data.map((item) => {
      if (!item.offer_id) return { ...item, offer_label: 'Básica' };
      const off = offers.find((o) => o.id === item.offer_id);
      return { ...item, offer_label: off ? `Oferta ${off.offer_number}` : `#${item.offer_id}` };
    }));
  };

  const equipmentHandlers = {
    onAdd: async (data) => {
      try { await equipmentsAPI.create(projectId, data); await loadEquipments(); }
      catch (err) { console.error('Error creating equipment:', err); }
    },
    onUpdate: async (id, data) => {
      try { await equipmentsAPI.update(id, data); await loadEquipments(); }
      catch (err) { console.error('Error updating equipment:', err); }
    },
    onDelete: async (id) => {
      try { await equipmentsAPI.delete(id); await loadEquipments(); }
      catch (err) { console.error('Error deleting equipment:', err); }
    },
  };

  const costFields = [
    {
      field: 'concept',
      label: 'Concepto / Partida',
      required: true,
      fullWidth: true,
      type: 'autocomplete',
      options: partidas.map((p) => ({ value: p.name, label: p.name })),
    },
    { field: 'real_cost', label: 'Coste Real (EUR)', type: 'number' },
    {
      field: 'real_cost_status', label: 'Estado del coste real', type: 'select',
      options: COST_STATUS_OPTIONS,
    },
    { field: 'offer_cost', label: 'Coste Oferta (EUR)', type: 'number' },
    { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
  ];

  const annotateCosts = (data) => data.map((item) => {
    if (!item.offer_id) return { ...item, offer_label: 'Básica' };
    const off = offers.find((o) => o.id === item.offer_id);
    return { ...item, offer_label: off ? `Oferta ${off.offer_number}` : `#${item.offer_id}` };
  });

  const costHandlers = {
    onAdd: async (data) => {
      try {
        await costEntriesAPI.create(projectId, data);
        const resp = await costEntriesAPI.list(projectId);
        setCosts(annotateCosts(resp.data));
      } catch (err) { console.error('Error creating cost entry:', err); }
    },
    onUpdate: async (id, data) => {
      try {
        await costEntriesAPI.update(id, data);
        const resp = await costEntriesAPI.list(projectId);
        setCosts(annotateCosts(resp.data));
      } catch (err) { console.error('Error updating cost entry:', err); }
    },
    onDelete: async (id) => {
      try {
        await costEntriesAPI.delete(id);
        setCosts(costs.filter((i) => i.id !== id));
      } catch (err) { console.error('Error deleting cost entry:', err); }
    },
  };

  // Add a partida as a new cost row (pre-filled concept)
  const addPartidaAsCost = async (partidaName) => {
    const alreadyExists = costs.some((c) => c.concept === partidaName);
    if (alreadyExists) return;
    try {
      await costEntriesAPI.create(projectId, { concept: partidaName, real_cost: null, offer_cost: null, notes: null });
      const resp = await costEntriesAPI.list(projectId);
      setCosts(annotateCosts(resp.data));
    } catch (err) { console.error('Error adding partida:', err); }
  };

  // Add all missing partidas at once
  const addAllPartidas = async () => {
    const existingConcepts = new Set(costs.map((c) => c.concept));
    const toAdd = partidas.filter((p) => !existingConcepts.has(p.name));
    for (const p of toAdd) {
      try {
        await costEntriesAPI.create(projectId, { concept: p.name, real_cost: null, offer_cost: null, notes: null });
      } catch (err) { console.error('Error adding partida:', err); }
    }
    if (toAdd.length > 0) {
      const resp = await costEntriesAPI.list(projectId);
      setCosts(annotateCosts(resp.data));
    }
  };

  const totalCost = costs.reduce((sum, c) => sum + (parseFloat(c.real_cost) || 0), 0);
  const totalOfferCost = costs.reduce((sum, c) => sum + (parseFloat(c.offer_cost) || 0), 0);
  const equipTotalReal = equipments.reduce((sum, e) => sum + (parseFloat(e.estimated_cost) || 0), 0);
  const equipTotalOffer = equipments.reduce((sum, e) => sum + (parseFloat(e.offer_cost) || 0), 0);
  const grandRealTotal = equipTotalReal + totalCost;
  const grandOfferTotal = equipTotalOffer + totalOfferCost;
  const salePrice = parseFloat(approvedAmount) || 0;
  const descuento = grandOfferTotal - salePrice;           // >0 = descuento aplicado
  const margin = salePrice - grandRealTotal;
  const marginPct = salePrice > 0 ? (margin / salePrice * 100) : 0;
  const fmtEUR = (v) => v.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleRefreshCosts = async () => {
    const totalReal =
      equipments.reduce((sum, e) => sum + (parseFloat(e.estimated_cost) || 0), 0) +
      costs.reduce((sum, c) => sum + (parseFloat(c.real_cost) || 0), 0);
    const totalOffer =
      equipments.reduce((sum, e) => sum + (parseFloat(e.offer_cost) || 0), 0) +
      costs.reduce((sum, c) => sum + (parseFloat(c.offer_cost) || 0), 0);
    setRefreshing(true);
    try {
      await engineeringAPI.updateBasic(projectId, {
        project_id: projectId,
        real_cost: totalReal,
        offer_cost: totalOffer,
      });
      // Reload from server to confirm persisted values
      const bResp = await engineeringAPI.getBasic(projectId);
      setFormData((prev) => ({
        ...prev,
        real_cost: bResp.data.real_cost ?? '',
        offer_cost: bResp.data.offer_cost ?? '',
      }));
    } catch (err) {
      console.error('Error al refrescar costes:', err);
      setError('Error al refrescar costes. Revisa la conexion.');
    } finally {
      setRefreshing(false);
    }
  };

  if (!projectId) return <Alert severity="warning">No se ha seleccionado un proyecto.</Alert>;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Diagramas de Proceso</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={3} label="Descripcion del diagrama de flujo (PFD)"
              name="process_flow_description" value={formData.process_flow_description} onChange={handleChange} onBlur={handleSave} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={3} label="Descripcion P&ID simplificado"
              name="pid_description" value={formData.pid_description} onChange={handleChange} onBlur={handleSave} />
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField fullWidth multiline rows={2} label="Especificaciones tecnicas generales"
              name="technical_specs" value={formData.technical_specs} onChange={handleChange} onBlur={handleSave} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Plazo estimado (semanas)" type="number"
              name="estimated_duration_weeks" value={formData.estimated_duration_weeks} onChange={handleChange} onBlur={handleSave} />
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#c62828', color: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#fff' }}>Resumen de Costes</Typography>
                <Tooltip title="Recalcular y guardar costes totales desde equipos y partidas">
                  <IconButton sx={{ color: '#fff' }} onClick={handleRefreshCosts} size="small" disabled={refreshing}>
                    {refreshing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <RefreshIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#fff' }}>Coste Real (Equipos)</Typography>
                  <Typography fontWeight="bold" sx={{ color: '#fff' }}>{fmtEUR(equipTotalReal)} €</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#fff' }}>Coste Real (Partidas)</Typography>
                  <Typography fontWeight="bold" sx={{ color: '#fff' }}>{fmtEUR(totalCost)} €</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#fff' }}>Coste Oferta (Equipos)</Typography>
                  <Typography fontWeight="bold" sx={{ color: '#fff' }}>{fmtEUR(equipTotalOffer)} €</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#fff' }}>Coste Oferta (Partidas)</Typography>
                  <Typography fontWeight="bold" sx={{ color: '#fff' }}>{fmtEUR(totalOfferCost)} €</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.3)' }} />
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#fff' }}>Total Coste Real</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>{fmtEUR(grandRealTotal)} €</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#fff' }}>Total Coste Oferta</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>{fmtEUR(grandOfferTotal)} €</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#fff' }}>Importe de Venta</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>{fmtEUR(salePrice)} €</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9 }}>Descuento (Oferta − Venta)</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: descuento >= 0 ? '#ffd54f' : '#a5d6a7' }}>
                    {fmtEUR(descuento)} €
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.3)' }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9 }}>Margen bruto (sobre Importe de Venta)</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: margin >= 0 ? '#a5d6a7' : '#ffab91' }}>
                    {fmtEUR(margin)} € {salePrice > 0 ? `(${marginPct.toFixed(1)}%)` : ''}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label="Notas"
              name="notes" value={formData.notes} onChange={handleChange} onBlur={handleSave} />
          </Grid>
        </Grid>
      </Paper>

      <EditableTable
        title="Lista de Equipos"
        columns={equipmentColumns}
        rows={equipments}
        notesField="notes"
        dialogFields={equipmentFields}
        emptyMessage="No hay equipos. Haz clic en Anadir para definir la lista."
        collapsible defaultCollapsed
        selectable
        selectedIds={selectedEquipmentIds}
        onSelectionChange={setSelectedEquipmentIds}
        headerExtra={
          <Button
            size="small" variant="contained" startIcon={<RequestQuoteIcon />}
            disabled={selectedEquipmentIds.length === 0}
            onClick={() => setRfqOpen(true)}
          >
            Pedir oferta{selectedEquipmentIds.length > 0 ? ` (${selectedEquipmentIds.length})` : ''}
          </Button>
        }
        summaryBanner={equipments.length > 0 ? (
          <Paper sx={{ px: 2, py: 0.5, backgroundColor: '#c62828', color: '#fff', display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#fff' }}>Coste Real:</Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: '#fff' }}>
                {equipTotalReal.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#fff' }}>Coste Oferta:</Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: '#fff' }}>
                {equipTotalOffer.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
              </Typography>
            </Box>
          </Paper>
        ) : null}
        {...equipmentHandlers}
      />

      <RequestQuotesDialog
        open={rfqOpen}
        onClose={() => setRfqOpen(false)}
        projectId={projectId}
        equipments={equipments.filter((e) => selectedEquipmentIds.includes(e.id))}
        supplierOptions={supplierOptions}
        onCreated={async () => { setSelectedEquipmentIds([]); await loadEquipments(); }}
      />

      {/* Partidas de coste configurables */}
      {partidas.length > 0 && (
        <Paper sx={{ p: 2, mt: 3, mb: 1, backgroundColor: '#c62828', color: '#fff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#fff' }}>
              Partidas disponibles para este tipo de proyecto:
            </Typography>
            <Button size="small" variant="outlined" onClick={addAllPartidas} sx={{ color: '#fff', borderColor: '#fff', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
              Anadir todas las partidas
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {partidas.map((p) => {
              const alreadyAdded = costs.some((c) => c.concept === p.name);
              return (
                <Chip
                  key={p.id}
                  label={p.name}
                  size="small"
                  color={alreadyAdded ? 'success' : 'default'}
                  variant={alreadyAdded ? 'filled' : 'outlined'}
                  onClick={alreadyAdded ? undefined : () => addPartidaAsCost(p.name)}
                  sx={{ cursor: alreadyAdded ? 'default' : 'pointer' }}
                />
              );
            })}
          </Box>
        </Paper>
      )}

      <EditableTable
        title="Estimacion de Costes por Partida"
        columns={costColumns}
        rows={costs}
        dialogFields={costFields}
        emptyMessage="No hay partidas de coste. Haz clic en Anadir o selecciona de la lista superior."
        collapsible defaultCollapsed
        summaryBanner={costs.length > 0 ? (
          <Paper sx={{ px: 2, py: 0.5, backgroundColor: '#c62828', color: '#fff', display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#fff' }}>Coste Real:</Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: '#fff' }}>
                {totalCost.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#fff' }}>Coste Oferta:</Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: '#fff' }}>
                {totalOfferCost.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
              </Typography>
            </Box>
          </Paper>
        ) : null}
        {...costHandlers}
      />
    </Box>
  );
}
